import React from 'react';

import { Box, SxProps, Theme, Typography } from '@mui/material';

export type GuidePrinciple = Readonly<{
  id: string;
  number: number;
  title: string;
  body: string;
}>;

type GuidePrinciplesGridProps = Readonly<{
  principles: readonly GuidePrinciple[];
  /** Fill down each column before wrapping, instead of left-to-right by row. */
  columnFlow?: boolean;
  /** Max columns once the container passes 600px. */
  columns?: 1 | 2;
}>;

export const GuidePrinciplesGrid: React.FC<GuidePrinciplesGridProps> = ({
  principles,
  columnFlow = false,
  columns = 2,
}) => (
  <Box sx={gridSx(columns, columnFlow ? Math.ceil(principles.length / 2) : undefined)}>
    {principles.map((principle) => (
      <Box key={principle.id} sx={itemSx}>
        <Typography variant="text-md-semibold" sx={titleSx}>
          {principle.number}. {principle.title}
        </Typography>
        <Typography variant="text-sm-regular" sx={bodySx}>
          {principle.body}
        </Typography>
      </Box>
    ))}
  </Box>
);

const gridSx =
  (columns: 1 | 2, rows?: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: backrs.spacing['spacing-3xl'],
    ...(columns === 2 && {
      '@container (min-width: 600px)': {
        gridTemplateColumns: '1fr 1fr',
        ...(rows ? { gridTemplateRows: `repeat(${rows}, auto)`, gridAutoFlow: 'column' } : {}),
      },
    }),
  });

const itemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
